// React
import React from 'react'

// Next
import Head from 'next/head'
import { GetStaticProps } from 'next'

// Utils
import { getLandingStaticProps } from '@/utils/ui/landing/get-static-props'

// External
import { useTranslation } from 'next-i18next'

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation('routes/privacy-policy')

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('meta.title'),
    description: t('meta.description'),
    publisher: {
      '@type': 'Organization',
      name: 'Supermeme.ai',
      logo: {
        '@type': 'ImageObject',
        url: 'https://supermeme.ai/static/logo-big.png',
      },
    },
    mainEntity: {
      '@type': 'WebPage',
      '@id': 'https://supermeme.ai/privacy-policy',
      lastReviewed: t('lastUpdated'),
    },
  }

  return (
    <div className='min-h-screen bg-gray-100'>
      <Head>
        <title>{t('meta.title')}</title>
        <meta name='description' content={t('meta.description')} />
        <link rel='icon' href='/favicon.ico' />
        <meta property='og:url' content='https://supermeme.ai/privacy-policy' />
        <link rel='canonical' href='https://supermeme.ai/privacy-policy' />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <main className='container mx-auto max-w-4xl px-4 py-8'>
        <h1 className='mb-6 text-3xl font-bold'>{t('title')}</h1>
        <p className='mb-4'>{t('lastUpdated')}</p>

        <section className='mb-8'>
          <p className='mb-4'>{t('introduction.p1')}</p>
          <p className='mb-4'>{t('introduction.p2')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('definitions.title')}
          </h2>
          <p className='mb-4'>{t('definitions.intro')}</p>
          <ul className='mb-4 list-disc pl-6'>
            {(t('definitions.terms', { returnObjects: true }) as string[]).map(
              (term, index) => (
                <li key={index}>{term}</li>
              )
            )}
          </ul>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('collectedInfo.title')}
          </h2>
          <p className='mb-4'>{t('collectedInfo.intro')}</p>
          <ul className='mb-4 list-disc pl-6'>
            {(
              t('collectedInfo.items', { returnObjects: true }) as string[]
            ).map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('infoUsage.title')}
          </h2>
          <p className='mb-4'>{t('infoUsage.intro')}</p>
          <ul className='mb-4 list-disc pl-6'>
            {(t('infoUsage.uses', { returnObjects: true }) as string[]).map(
              (use, index) => (
                <li key={index}>{use}</li>
              )
            )}
          </ul>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('thirdPartyInfo.endUserTitle')}
          </h2>
          <p className='mb-4'>{t('thirdPartyInfo.endUserP1')}</p>
          <p className='mb-4'>{t('thirdPartyInfo.endUserP2')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('thirdPartyInfo.customerTitle')}
          </h2>
          <p className='mb-4'>{t('thirdPartyInfo.customerText')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>{t('sharing.title')}</h2>
          <p className='mb-4'>{t('sharing.p1')}</p>
          <p className='mb-4'>{t('sharing.p2')}</p>
          <p className='mb-4'>{t('sharing.p3')}</p>
          <p className='mb-4'>{t('sharing.p4')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('infoCollection.title')}
          </h2>
          <p className='mb-4'>{t('infoCollection.text')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('emailUsage.title')}
          </h2>
          <p className='mb-4'>{t('emailUsage.text')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('infoRetention.title')}
          </h2>
          <p className='mb-4'>{t('infoRetention.text')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('infoProtection.title')}
          </h2>
          <p className='mb-4'>{t('infoProtection.text')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('internationalTransfer.title')}
          </h2>
          <p className='mb-4'>{t('internationalTransfer.text')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>{t('security.title')}</h2>
          <p className='mb-4'>{t('security.text')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('infoUpdate.title')}
          </h2>
          <p className='mb-4'>{t('infoUpdate.p1')}</p>
          <p className='mb-4'>{t('infoUpdate.p2')}</p>
          <p className='mb-4'>{t('infoUpdate.p3')}</p>
          <p className='mb-4'>{t('infoUpdate.p4')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('personnel.title')}
          </h2>
          <p className='mb-4'>{t('personnel.p1')}</p>
          <p className='mb-4'>{t('personnel.p2')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>{t('sale.title')}</h2>
          <p className='mb-4'>{t('sale.text')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>
            {t('affiliates.title')}
          </h2>
          <p className='mb-4'>{t('affiliates.text')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>{t('law.title')}</h2>
          <p className='mb-4'>{t('law.p1')}</p>
          <p className='mb-4'>{t('law.p2')}</p>
          <p className='mb-4'>{t('law.p3')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>{t('consent.title')}</h2>
          <p className='mb-4'>{t('consent.text')}</p>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>{t('links.title')}</h2>
        </section>

        <section className='mb-8'>
          <h2 className='mb-4 text-2xl font-semibold'>{t('contact.title')}</h2>
          <p className='mb-4'>{t('contact.text')}</p>
          <p>{t('contact.email')}</p>
        </section>
      </main>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return getLandingStaticProps({
    locale,
    namespaces: ['common', 'routes/privacy-policy'],
  })
}

export default PrivacyPolicy
